import React from 'react';
import { PenLine, X, Sparkles, Search, FileText, BookOpen, ChevronRight } from 'lucide-react';

interface SmartSelectionToolbarProps {
  position: { top: number; left: number };
  selectedText: string;
  onAnnotate: () => void;
  onSearch?: (text: string) => void;
  onFeatureSoon?: (featureName: string) => void;
  onClose: () => void;
  canAnnotate?: boolean;
}

export const SmartSelectionToolbar: React.FC<SmartSelectionToolbarProps> = ({
  position,
  selectedText,
  onAnnotate,
  onSearch,
  onFeatureSoon,
  onClose,
  canAnnotate = true,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Écouteur pour fermer avec la touche Échap
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!selectedText.trim()) return null;

  const handleSoon = (featureName: string) => {
    if (onFeatureSoon) {
      onFeatureSoon(featureName);
    }
  };

  const isNearTop = position.top < 300;
  const clampedLeft = Math.max(140, Math.min((typeof window !== 'undefined' ? window.innerWidth : 1000) - 140, position.left));

  return (
    <div
      ref={panelRef}
      style={{
        top: `${Math.max(10, position.top)}px`,
        left: `${clampedLeft}px`,
        transform: isNearTop ? 'translate(-50%, 12px)' : 'translate(-50%, -100%) translateY(-8px)',
      }}
      className="fixed z-[10001] flex flex-col bg-zinc-950/95 text-white p-1.5 sm:p-2 rounded-xl shadow-2xl border border-zinc-750/90 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150 select-none w-44 sm:w-48 max-w-[90vw]"
      onMouseDown={(e) => {
        // Empêcher la perte de sélection lors du clic sur le panneau
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Header : Titre Smart Selection & Fermeture */}
      <div className="flex items-center justify-between pb-1 mb-1 border-b border-zinc-800/80">
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-400">
          <Sparkles className="w-2.5 h-2.5 text-amber-400" />
          <span className="tracking-wide">Docarn Smart</span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="text-zinc-400 hover:text-white hover:bg-zinc-800 p-0.5 rounded transition-colors cursor-pointer"
          title="Fermer le menu (Échap)"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Extrait court du texte sélectionné */}
      <div className="text-[8.5px] text-zinc-400 bg-zinc-900/90 px-1.5 py-0.5 rounded truncate italic mb-1 border border-zinc-800/60 font-mono">
        « {selectedText.trim()} »
      </div>

      {/* Liste des Actions Verticales Compactes */}
      <div className="flex flex-col gap-1">
        {/* 1. Action Annoter (Active) */}
        {canAnnotate ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAnnotate();
            }}
            className="flex items-center justify-between w-full px-2 py-1.2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-zinc-950 font-bold text-[10.5px] rounded-lg shadow-sm transition-all active:scale-[0.98] cursor-pointer group"
            title="Annoter ce passage sélectionné"
          >
            <div className="flex items-center gap-1.5">
              <PenLine className="w-3 h-3" />
              <span>Annoter</span>
            </div>
            <ChevronRight className="w-2.5 h-2.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
          </button>
        ) : (
          <div className="text-[9px] text-zinc-400 px-1.5 py-1 italic bg-zinc-900 rounded-lg text-center">
            Réservé aux abonnés
          </div>
        )}

        {/* 2. Action Rechercher dans le texte */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onSearch) {
              onSearch(selectedText);
            } else {
              handleSoon('La recherche contextuelle');
            }
          }}
          className="flex items-center justify-between w-full px-2 py-1.2 bg-zinc-900 hover:bg-zinc-800/90 text-zinc-100 font-semibold text-[10.5px] rounded-lg border border-zinc-750/80 hover:border-zinc-600 transition-all active:scale-[0.98] cursor-pointer group"
          title="Rechercher ce texte dans l'acte"
        >
          <div className="flex items-center gap-1.5">
            <Search className="w-3 h-3 text-primary-400 group-hover:text-primary-300" />
            <span>Rechercher</span>
          </div>
          <span className="text-[8.5px] text-zinc-400 group-hover:text-zinc-200">Volet</span>
        </button>

        {/* Séparateur pour les fonctionnalités IA */}
        <div className="pt-0.5 pb-0.5 px-0.5 flex items-center justify-between text-[7.5px] font-bold text-zinc-400 uppercase tracking-wider">
          <span>IA & Outils</span>
          <span className="text-[6.5px] bg-zinc-800 text-zinc-400 px-1 py-0.2 rounded font-mono">Bêta</span>
        </div>

        {/* 3. Action Analyser IA */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSoon("L'analyse juridique IA");
          }}
          className="flex items-center justify-between w-full px-1.5 py-0.5 text-zinc-300 hover:text-white hover:bg-zinc-800/70 font-medium text-[9.5px] rounded-md transition-all cursor-pointer group"
          title="Analyser juridiquement ce passage avec l'IA"
        >
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-2.5 h-2.5 text-amber-400/90" />
            <span>Analyser</span>
          </div>
          <span className="text-[7.5px] text-zinc-400 group-hover:text-amber-300 bg-zinc-800/60 px-1 py-0.2 rounded">Bientôt</span>
        </button>

        {/* 4. Action Résumer */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSoon('Le résumé intelligent');
          }}
          className="flex items-center justify-between w-full px-1.5 py-0.5 text-zinc-300 hover:text-white hover:bg-zinc-800/70 font-medium text-[9.5px] rounded-md transition-all cursor-pointer group"
          title="Résumer ce passage avec l'IA"
        >
          <div className="flex items-center gap-1.5">
            <FileText className="w-2.5 h-2.5 text-zinc-400" />
            <span>Résumer</span>
          </div>
          <span className="text-[7.5px] text-zinc-400 group-hover:text-amber-300 bg-zinc-800/60 px-1 py-0.2 rounded">Bientôt</span>
        </button>

        {/* 5. Action Définir */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSoon('La définition juridique');
          }}
          className="flex items-center justify-between w-full px-1.5 py-0.5 text-zinc-300 hover:text-white hover:bg-zinc-800/70 font-medium text-[9.5px] rounded-md transition-all cursor-pointer group"
          title="Définition des termes juridiques"
        >
          <div className="flex items-center gap-1.5">
            <BookOpen className="w-2.5 h-2.5 text-zinc-400" />
            <span>Définir</span>
          </div>
          <span className="text-[7.5px] text-zinc-400 group-hover:text-amber-300 bg-zinc-800/60 px-1 py-0.2 rounded">Bientôt</span>
        </button>
      </div>
    </div>
  );
};
